import {
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";

type Option = { id: string; label: string };

type FormState = {
    question_text: string;
    helper_text: string;
    placeholder: string;
    input_type: string;
    is_required: boolean;
    is_active: boolean;
    options: Option[];
    link: { text: string; url: string };
};

type Props = {
    form: FormState;
    setForm: (next: FormState) => void;
    onSubmit: (event: any) => void;
    onCancel: () => void;
    saving?: boolean;
    submitLabel?: string;
};

const INPUT_TYPES = [
    {
        value: "textarea",
        label: "Textarea",
        hint: "Multi-line free text (e.g. notes, joy / unwind)",
    },
    {
        value: "boolean",
        label: "Boolean (Yes / No)",
        hint: "Two-option pills — typically Yes and No",
    },
    {
        value: "mcq",
        label: "MCQ (single select)",
        hint: "One choice from a list of options",
    },
    {
        value: "checkbox",
        label: "Checkbox",
        hint: "Consent / multi-select style answers; supports link text",
    },
];

export const PreSessionQuestionForm = ({
    form,
    setForm,
    onSubmit,
    onCancel,
    saving = false,
    submitLabel = "Save",
}: Props) => {
    const needsOptions = ["boolean", "mcq", "checkbox"].includes(form.input_type);
    const typeMeta = INPUT_TYPES.find((t) => t.value === form.input_type);

    const updateField = (name: string, value: any) => {
        const next = { ...form, [name]: value };
        if (name === "input_type") {
            if (value === "boolean" && (!form.options || form.options.length === 0)) {
                next.options = [
                    { id: "yes", label: "Yes" },
                    { id: "no", label: "No" },
                ];
            }
            if (value === "textarea") {
                next.options = [];
            }
        }
        setForm(next);
    };

    const updateOption = (index: number, field: "id" | "label", value: string) => {
        const options = [...(form.options || [])];
        options[index] = { ...options[index], [field]: value };
        setForm({ ...form, options });
    };

    const addOption = () => {
        setForm({
            ...form,
            options: [
                ...(form.options || []),
                { id: `opt_${Date.now()}`, label: "" },
            ],
        });
    };

    const removeOption = (index: number) => {
        const options = [...(form.options || [])];
        options.splice(index, 1);
        setForm({ ...form, options });
    };

    return (
        <Box
            as="form"
            onSubmit={onSubmit}
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="#d5e0e0"
            boxShadow="sm"
            overflow="hidden"
            maxW="920px"
        >
            <Box bg="#e8f2f2" px={6} py={4} borderBottom="1px solid" borderColor="#d5e0e0">
                <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                    <Box>
                        <Text fontWeight="700" color="#1f3d3d">
                            Question details
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            Fields marked * are required for saving.
                        </Text>
                    </Box>
                    <HStack spacing={2}>
                        <Badge colorScheme={TYPE_COLORS(form.input_type)} textTransform="uppercase">
                            {form.input_type}
                        </Badge>
                        <Badge colorScheme={form.is_required ? "red" : "gray"}>
                            {form.is_required ? "Required" : "Optional"}
                        </Badge>
                        <Badge colorScheme={form.is_active ? "green" : "red"}>
                            {form.is_active ? "Active" : "Inactive"}
                        </Badge>
                    </HStack>
                </Flex>
            </Box>

            <Stack spacing={6} p={6}>
                <FormControl isRequired>
                    <FormLabel fontWeight="600" color="#1f3d3d">
                        Question text
                    </FormLabel>
                    <Textarea
                        value={form.question_text}
                        onChange={(e) => updateField("question_text", e.target.value)}
                        placeholder="e.g. Have you attended therapy before?"
                        rows={2}
                        borderColor="#c9d6d6"
                        _focus={{ borderColor: "#327171", boxShadow: "0 0 0 1px #327171" }}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel fontWeight="600" color="#1f3d3d">
                        Helper text
                    </FormLabel>
                    <Textarea
                        value={form.helper_text}
                        onChange={(e) => updateField("helper_text", e.target.value)}
                        placeholder="Short guidance shown under the question"
                        rows={2}
                        borderColor="#c9d6d6"
                        _focus={{ borderColor: "#327171", boxShadow: "0 0 0 1px #327171" }}
                    />
                    <FormHelperText>
                        Shown below the question on the mobile form.
                    </FormHelperText>
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <FormControl isRequired>
                        <FormLabel fontWeight="600" color="#1f3d3d">
                            Input type
                        </FormLabel>
                        <Select
                            value={form.input_type}
                            onChange={(e) => updateField("input_type", e.target.value)}
                            borderColor="#c9d6d6"
                            _focus={{ borderColor: "#327171", boxShadow: "0 0 0 1px #327171" }}
                        >
                            {INPUT_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </Select>
                        {typeMeta ? (
                            <FormHelperText>{typeMeta.hint}</FormHelperText>
                        ) : null}
                    </FormControl>

                    <FormControl isDisabled={form.input_type !== "textarea"}>
                        <FormLabel fontWeight="600" color="#1f3d3d">
                            Placeholder
                        </FormLabel>
                        <Input
                            value={form.placeholder}
                            onChange={(e) => updateField("placeholder", e.target.value)}
                            placeholder="Type here..."
                            borderColor="#c9d6d6"
                            _focus={{ borderColor: "#327171", boxShadow: "0 0 0 1px #327171" }}
                        />
                        <FormHelperText>Only used for textarea questions.</FormHelperText>
                    </FormControl>
                </SimpleGrid>

                <Box
                    bg="#f7fafa"
                    border="1px solid"
                    borderColor="#d5e0e0"
                    borderRadius="md"
                    p={4}
                >
                    <Text fontWeight="600" color="#1f3d3d" mb={3}>
                        Visibility & validation
                    </Text>
                    <HStack spacing={8} flexWrap="wrap">
                        <Checkbox
                            isChecked={form.is_required}
                            onChange={(e) => updateField("is_required", e.target.checked)}
                            colorScheme="teal"
                            size="lg"
                        >
                            Required on mobile form
                        </Checkbox>
                        <Checkbox
                            isChecked={form.is_active}
                            onChange={(e) => updateField("is_active", e.target.checked)}
                            colorScheme="teal"
                            size="lg"
                        >
                            Active (shown to users)
                        </Checkbox>
                    </HStack>
                </Box>

                <Box>
                    <Text fontWeight="600" color="#1f3d3d" mb={1}>
                        Optional link (for consent / terms)
                    </Text>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                        Use when the label should highlight a clickable phrase (e.g. Therapy
                        Session Terms).
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                            <FormLabel fontSize="sm">Link text</FormLabel>
                            <Input
                                value={form.link?.text || ""}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        link: {
                                            ...(form.link || { text: "", url: "" }),
                                            text: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Therapy Session Terms"
                                borderColor="#c9d6d6"
                                _focus={{
                                    borderColor: "#327171",
                                    boxShadow: "0 0 0 1px #327171",
                                }}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Link URL</FormLabel>
                            <Input
                                value={form.link?.url || ""}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        link: {
                                            ...(form.link || { text: "", url: "" }),
                                            url: e.target.value,
                                        },
                                    })
                                }
                                placeholder="/terms-of-use"
                                borderColor="#c9d6d6"
                                _focus={{
                                    borderColor: "#327171",
                                    boxShadow: "0 0 0 1px #327171",
                                }}
                            />
                        </FormControl>
                    </SimpleGrid>
                </Box>

                {needsOptions ? (
                    <Box>
                        <Flex justify="space-between" align="center" mb={3}>
                            <Box>
                                <Text fontWeight="600" color="#1f3d3d">
                                    Answer options
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                    Id is stored with answers; label is shown in the app.
                                </Text>
                            </Box>
                            <Button
                                size="sm"
                                leftIcon={<AddIcon />}
                                variant="outline"
                                borderColor="#327171"
                                color="#327171"
                                onClick={addOption}
                            >
                                Add option
                            </Button>
                        </Flex>
                        <VStack spacing={3} align="stretch">
                            {(form.options || []).map((opt, index) => (
                                <Flex
                                    key={`${opt.id}-${index}`}
                                    gap={3}
                                    align="center"
                                    bg="#f7fafa"
                                    p={3}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="#d5e0e0"
                                >
                                    <Box flex="0 0 140px">
                                        <Text fontSize="xs" color="gray.500" mb={1}>
                                            Id
                                        </Text>
                                        <Input
                                            size="sm"
                                            value={opt.id}
                                            onChange={(e) => updateOption(index, "id", e.target.value)}
                                            bg="white"
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <Text fontSize="xs" color="gray.500" mb={1}>
                                            Label
                                        </Text>
                                        <Input
                                            size="sm"
                                            value={opt.label}
                                            onChange={(e) =>
                                                updateOption(index, "label", e.target.value)
                                            }
                                            bg="white"
                                            placeholder="Option label"
                                        />
                                    </Box>
                                    <IconButton
                                        aria-label="Remove option"
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        mt={5}
                                        onClick={() => removeOption(index)}
                                    />
                                </Flex>
                            ))}
                            {!form.options?.length ? (
                                <Box
                                    border="1px dashed"
                                    borderColor="#b7caca"
                                    borderRadius="md"
                                    p={4}
                                    textAlign="center"
                                    color="gray.500"
                                >
                                    No options yet. Add Yes/No or choice labels for this type.
                                </Box>
                            ) : null}
                        </VStack>
                    </Box>
                ) : null}
            </Stack>

            <Divider />

            <Flex
                px={6}
                py={4}
                gap={3}
                bg="#fafcfc"
                justify="flex-start"
                flexWrap="wrap"
            >
                <Button
                    type="submit"
                    bg="#327171"
                    color="white"
                    _hover={{ bg: "#2a5f5f" }}
                    _active={{ bg: "#244f4f" }}
                    isLoading={saving}
                    px={8}
                >
                    {submitLabel}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    borderColor="#98b0b0"
                    color="#327171"
                    onClick={onCancel}
                    isDisabled={saving}
                >
                    Cancel
                </Button>
            </Flex>
        </Box>
    );
};

function TYPE_COLORS(type: string) {
    if (type === "textarea") return "purple";
    if (type === "boolean") return "blue";
    if (type === "mcq") return "orange";
    if (type === "checkbox") return "teal";
    return "gray";
}
